/**
 * Lightweight toast system. No external dependencies.
 *
 * Usage:
 *   import { ToastProvider, useToast } from '../lib/toast';
 *   const toast = useToast();
 *   toast.show('Settings saved', 'ok');
 *   toast.show('Order rejected', 'bad');
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

type Tone = 'ok' | 'bad' | 'warn' | 'info';

// v0.5.5 AE-081: optional action button so toasts can carry a CTA
// ("View in Portfolio") to bridge post-action navigation.
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  action?: ToastAction;
  /** Milliseconds before auto-dismiss. Default 3500. Toasts with an
   *  action default to 6000ms so the operator has time to click. */
  durationMs?: number;
}

interface Toast {
  id: number;
  message: string;
  tone: Tone;
  action?: ToastAction;
}

interface ToastCtx {
  show: (message: string, tone?: Tone, options?: ToastOptions) => void;
}

const Ctx = createContext<ToastCtx | null>(null);
let nextId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback(
    (message: string, tone: Tone = 'info', options?: ToastOptions) => {
      const id = ++nextId;
      const action = options?.action;
      setToasts((prev) => [...prev, { id, message, tone, action }]);
      const duration = options?.durationMs ?? (action ? 6000 : 3500);
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), duration);
    },
    [],
  );

  const value = useMemo(() => ({ show }), [show]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="fixed bottom-4 right-4 z-[999] flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto card px-4 py-2.5 text-sm font-medium
              animate-[slideIn_0.2s_ease-out] border-l-4 max-w-sm flex items-center gap-3
              ${t.tone === 'ok' ? 'border-ok text-ok' : ''}
              ${t.tone === 'bad' ? 'border-bad text-bad' : ''}
              ${t.tone === 'warn' ? 'border-warn text-warn' : ''}
              ${t.tone === 'info' ? 'border-accent text-ink' : ''}`}
          >
            <span className="flex-1">{t.message}</span>
            {t.action && (
              <button
                className="text-[11px] underline whitespace-nowrap hover:opacity-80"
                onClick={() => {
                  t.action!.onClick();
                  setToasts((prev) => prev.filter((x) => x.id !== t.id));
                }}
                data-testid={`toast-action-${t.id}`}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}
